import { Immutable } from "immer";
import React from "react";

import { constants } from "@reactivated/constants";

import { getDocument } from "@thelabnyc/thelabui/src/utils/dom";

import { RatingGraphic } from "../../../components/RatingGraphic";
import { UGCCarousel } from "../../../components/UGCCarousel";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { IFormUUID, IReviewsProductTypeID } from "../../../models/nominals";
import { UGCListSubBlock } from "../../../models/ugc";
import { roundReviewRating } from "../../../utils/math";
import { usePageSetting } from "../../../utils/settings";
import { CarouselReviewList } from "../CarouselReviewList";
import { LoadMoreButton } from "../LoadMoreButton";
import { ReviewList } from "../ReviewList";
import { ReviewStarRating } from "../ReviewStarRating";
import { SidebarFilterSection } from "../SidebarFilterSection";
import { TopBar } from "../TopBar";
import { TopFilterSection } from "../TopFilterSection";
import { WriteReviewButton } from "../WriteReviewButton";
import { WriteReviewForm } from "../WriteReviewForm";
import { WriteReviewThankYou } from "../WriteReviewThankYou";
import { ReviewsComponentTypes } from "../constants";

import styles from "./ProductReviewsContent.module.scss";

const AppSlug = constants["tsicommon.types.AppSlug"];

const MAX_SEARCH_KEYWORDS_NUMBER = 6;

const buildSideNav = (
    displayedProductTypeIDWhitelist: readonly IReviewsProductTypeID[],
    showProductsFilter: boolean,
    showSourcesFilter: boolean,
    starHasStroke: boolean | undefined,
    isCollapsedView: boolean | undefined,
): React.JSX.Element => {
    return (
        <SidebarFilterSection
            isMobile={false}
            displayedProductTypeIDWhitelist={displayedProductTypeIDWhitelist}
            showProducts={showProductsFilter}
            showSources={showSourcesFilter}
            starHasStroke={starHasStroke}
            writeReviewURL={""}
            onToggleMobileSideNav={() => {}}
            mobileSideNavOpen={true}
            showAppliedFilters={false}
            isCollapsedView={isCollapsedView}
        />
    );
};

const buildTopFilterNav = (
    displayedProductTypeIDWhitelist: readonly IReviewsProductTypeID[],
    showProductsFilter: boolean,
    showSourcesFilter: boolean,
    reviewsComponentType: ReviewsComponentTypes,
): React.JSX.Element => {
    return (
        <TopFilterSection
            isMobile={false}
            displayedProductTypeIDWhitelist={displayedProductTypeIDWhitelist}
            showProducts={showProductsFilter}
            showSources={showSourcesFilter}
            maxKeywordsNumber={MAX_SEARCH_KEYWORDS_NUMBER}
            isCarouselComponentType={
                reviewsComponentType === ReviewsComponentTypes.CAROUSEL
            }
        />
    );
};

const buildReviews = (
    starHasStroke: boolean | undefined,
): React.JSX.Element => {
    return (
        <ReviewList
            showSideDate={true}
            enablePDPLinks={false}
            showRecommended={false}
            showVerboseUserInfo={false}
            starHasStroke={starHasStroke}
        />
    );
};

const buildLoadMoreButton = (onLoadMore: () => void): React.JSX.Element => {
    return <LoadMoreButton onLoadMore={onLoadMore} />;
};

const buildUGCCarousel = (
    ugcCarouselPosts: readonly UGCListSubBlock[],
    ugcCarouselDesc: string,
): React.JSX.Element | null => {
    if (!ugcCarouselPosts || ugcCarouselPosts.length < 1) {
        return null;
    }

    return (
        <div
            role="region"
            aria-label="Testimonials Carousel"
            className="ugc-carousel-container ugc-carousel-container__reviews-full l-full-width"
        >
            {ugcCarouselDesc && (
                <section
                    className="ugc-carousel__header"
                    dangerouslySetInnerHTML={{
                        __html: ugcCarouselDesc,
                    }}
                />
            )}
            {ugcCarouselPosts.length > 0 && (
                <UGCCarousel tiles={ugcCarouselPosts} />
            )}
        </div>
    );
};

const buildRatingGraphic = (
    averageRating: number,
    starHasStroke: boolean | undefined,
): React.JSX.Element => {
    return (
        <div className="customer-review__stars customer-review__stars--mobile">
            <RatingGraphic
                cardClass="product-reviews-header"
                cardSize="large"
                rating={averageRating}
                onlyStars={true}
                starHasStroke={starHasStroke}
            />
        </div>
    );
};

const buildReviewList = (
    starHasStroke: boolean | undefined,
    onLoadMore: () => void,
): React.JSX.Element => {
    return (
        <div className={styles.reviewList}>
            {buildReviews(starHasStroke)}
            {buildLoadMoreButton(onLoadMore)}
        </div>
    );
};

const buildCarouselReviewList = (
    starHasStroke: boolean | undefined,
    onLoadMore: () => void,
): React.JSX.Element => {
    return (
        <CarouselReviewList
            showSideDate={true}
            enablePDPLinks={false}
            starHasStroke={starHasStroke}
            onLoadMore={onLoadMore}
            maxKeywordsNumber={MAX_SEARCH_KEYWORDS_NUMBER}
        />
    );
};

export const ProductReviews = (
    props: Immutable<{
        displayedProductTypeIDWhitelist: readonly IReviewsProductTypeID[];
        title: string;
        numReviews: number;
        averageRating: number;
        showWriteReviewButton: boolean;
        ugcCarouselDesc: string;
        ugcCarouselPosts: readonly UGCListSubBlock[];
        brand: string;
        showProductsFilter: boolean;
        showSourcesFilter: boolean;
        writeReviewFormUUID: IFormUUID;
        writeReviewFormTitle: string;
        starHasStroke?: boolean;
        reviewsComponentType?: ReviewsComponentTypes;
        isCollapsedView?: boolean;
    }> & {
        onLoadMore: () => void;
    },
): React.ReactNode => {
    const appSlug = usePageSetting("app_slug");
    const titleRef = React.useRef<HTMLDivElement>(null);
    const reviewsComponentType =
        props.reviewsComponentType ?? ReviewsComponentTypes.CLASSIC;

    useMountEffect(() => {
        const document = getDocument();
        if (!document) {
            return;
        }
        const scrollToReviews = document.body.getAttribute("scrollToReviews");
        const el = document.querySelector<HTMLElement>("#reviews__body");
        if (el && scrollToReviews) {
            el?.scrollIntoView(true);
            document.body.removeAttribute("scrollToReviews");
        }
    });

    const onReviewSubmit = (): void => {
        if (titleRef.current) {
            titleRef.current.scrollIntoView({ behavior: "smooth" });
        }
    };

    if (appSlug === AppSlug.TEMPURCA) {
        return null;
    }

    return (
        <div className="customer-review l-capped-width">
            <div className="customer-review__header customer-review__header--pdp">
                {reviewsComponentType !== ReviewsComponentTypes.CAROUSEL &&
                    buildRatingGraphic(
                        props.averageRating,
                        props.starHasStroke,
                    )}
                {props.title && (
                    <h2 className={styles.title} ref={titleRef}>
                        {props.title}
                    </h2>
                )}
                {buildUGCCarousel(
                    props.ugcCarouselPosts,
                    props.ugcCarouselDesc,
                )}
                <ReviewStarRating
                    roundedRating={roundReviewRating(props.averageRating)}
                    numReviews={props.numReviews}
                    averageRating={props.averageRating}
                    starHasStroke={props.starHasStroke}
                    reviewsComponentType={reviewsComponentType}
                />
                {props.showWriteReviewButton && (
                    <>
                        <WriteReviewButton
                            formUUID={props.writeReviewFormUUID}
                        />
                        <WriteReviewForm
                            formUUID={props.writeReviewFormUUID}
                            formTitle={props.writeReviewFormTitle}
                            brand={props.brand}
                            onReviewSubmit={onReviewSubmit}
                        />
                        <WriteReviewThankYou
                            formUUID={props.writeReviewFormUUID}
                        />
                    </>
                )}
            </div>
            <div
                id="reviews__body"
                className="customer-review__body"
                tabIndex={-1}
            >
                {reviewsComponentType !== ReviewsComponentTypes.CAROUSEL
                    ? buildSideNav(
                          props.displayedProductTypeIDWhitelist,
                          props.showProductsFilter,
                          props.showSourcesFilter,
                          props.starHasStroke,
                          props.isCollapsedView,
                      )
                    : buildTopFilterNav(
                          props.displayedProductTypeIDWhitelist,
                          props.showProductsFilter,
                          props.showSourcesFilter,
                          reviewsComponentType,
                      )}
                <div className={`${styles.content} customer-review__content`}>
                    <TopBar />
                    {reviewsComponentType !== ReviewsComponentTypes.CAROUSEL
                        ? buildReviewList(props.starHasStroke, props.onLoadMore)
                        : buildCarouselReviewList(
                              props.starHasStroke,
                              props.onLoadMore,
                          )}
                </div>
            </div>
        </div>
    );
};
